import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import API from "../../Api";

export default function SettingsSecurity() {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState("status");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/api/security/mfa");
      setStatus(res.data || { enabled: false, type: null });
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Could not load MFA status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startSetup = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await API.post("/api/security/mfa/setup");
      setSetup(res.data);
      setStep("setup");
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Could not start MFA setup");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await API.post("/api/security/mfa/confirm", { code });
      setRecoveryCodes(res.data?.recoveryCodes || []);
      setSaved(false);
      setStep("codes");
      setStatus({ enabled: true, type: "TOTP" });
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "The verification code is invalid or expired.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await API.post("/api/security/mfa/disable", { password, code });
      setMessage("MFA has been disabled.");
      setPassword("");
      setCode("");
      setStep("status");
      setStatus({ enabled: false, type: null, administrative: status?.administrative });
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Could not disable MFA");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await API.post("/api/security/mfa/recovery/regenerate", { password, code });
      setRecoveryCodes(res.data?.recoveryCodes || []);
      setSaved(false);
      setPassword("");
      setCode("");
      setStep("codes");
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Could not regenerate recovery codes");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="settings-panel">Loading security…</div>;
  }

  return (
    <div className="settings-panel">
      <h2>Multi-Factor Authentication</h2>
      <p className="settings-subtitle">Protect your Commray account with an authenticator app.</p>
      {error && <div className="settings-alert settings-alert--error">{error}</div>}
      {message && <div className="settings-alert settings-alert--success">{message}</div>}

      {step === "status" && (
        <>
          <div className="settings-field">
            <label>Status</label>
            <div className="settings-readonly">{status?.enabled ? "Enabled" : "Disabled"}</div>
          </div>
          {status?.enabled && (
            <div className="settings-field">
              <label>Method</label>
              <div className="settings-readonly">Authenticator app · TOTP</div>
            </div>
          )}
          {status?.administrative && !status?.enabled && (
            <p className="settings-subtitle">Administrator accounts should turn on an authenticator.</p>
          )}
          <div className="settings-actions">
            {status?.enabled ? (
              <button type="button" className="settings-btn settings-btn--primary" onClick={() => { setStep("manage"); setError(""); }}>
                Manage MFA
              </button>
            ) : (
              <button type="button" className="settings-btn settings-btn--primary" onClick={startSetup} disabled={busy}>
                Set up authenticator
              </button>
            )}
          </div>
        </>
      )}

      {step === "setup" && (
        <>
          <ol className="mfa-steps">
            <li>Install an authenticator app</li>
            <li>Scan the QR code below</li>
          </ol>
          {setup?.qrCode && (
            <div className="mfa-qr">
              <QRCodeSVG value={setup.qrCode} size={180} />
            </div>
          )}
          <p className="settings-subtitle">Can&apos;t scan the QR code?</p>
          <div className="settings-field">
            <label>Manual setup key</label>
            <div className="settings-readonly mfa-secret">{setup?.manualEntryKey}</div>
          </div>
          <div className="settings-actions">
            <button type="button" className="settings-btn settings-btn--secondary" onClick={() => setStep("status")}>Back</button>
            <button type="button" className="settings-btn settings-btn--primary" onClick={() => { setStep("verify"); setError(""); }}>Continue</button>
          </div>
        </>
      )}

      {step === "verify" && (
        <form onSubmit={confirm}>
          <p className="settings-subtitle">Enter the 6-digit code displayed by your authenticator app.</p>
          <div className="settings-field">
            <label htmlFor="mfa-confirm-code">Authenticator code</label>
            <input
              id="mfa-confirm-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
            />
          </div>
          <div className="settings-actions">
            <button type="button" className="settings-btn settings-btn--secondary" onClick={() => setStep("setup")}>Back</button>
            <button type="submit" className="settings-btn settings-btn--primary" disabled={busy}>Verify and enable MFA</button>
          </div>
        </form>
      )}

      {step === "codes" && (
        <>
          <p className="settings-subtitle">
            MFA enabled successfully. Save your recovery codes somewhere secure. Each code works once if you lose
            access to your authenticator. Commray cannot show these codes again.
          </p>
          <ul className="mfa-codes">
            {recoveryCodes.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <div className="settings-actions">
            <button type="button" className="settings-btn settings-btn--secondary" onClick={() => downloadCodes(recoveryCodes)}>Download codes</button>
            <button type="button" className="settings-btn settings-btn--secondary" onClick={() => navigator.clipboard.writeText(recoveryCodes.join("\n"))}>Copy codes</button>
          </div>
          <label className="settings-field" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
            <span>I have saved my recovery codes</span>
          </label>
          <button
            type="button"
            className="settings-btn settings-btn--primary"
            disabled={!saved}
            onClick={() => {
              setRecoveryCodes([]);
              setStep("status");
              setMessage("MFA is enabled.");
            }}
          >
            Continue
          </button>
        </>
      )}

      {step === "manage" && (
        <>
          <p className="settings-subtitle">Status: Enabled. Authenticator app · TOTP</p>
          <div className="settings-actions">
            <button type="button" className="settings-btn settings-btn--secondary" onClick={() => setStep("regenerate")}>Regenerate recovery codes</button>
            <button type="button" className="settings-btn settings-btn--danger" onClick={() => setStep("disable")}>Disable MFA</button>
          </div>
          <button type="button" className="settings-btn settings-btn--secondary" onClick={() => setStep("status")}>Back</button>
        </>
      )}

      {step === "disable" && (
        <form onSubmit={disable}>
          <h3>Disable Multi-Factor Authentication</h3>
          <p className="settings-subtitle">For your security, confirm your password and current authenticator code.</p>
          <div className="settings-field">
            <label htmlFor="mfa-disable-password">Password</label>
            <input id="mfa-disable-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <div className="settings-field">
            <label htmlFor="mfa-disable-code">Authenticator code</label>
            <input id="mfa-disable-code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required />
          </div>
          <div className="settings-actions">
            <button type="button" className="settings-btn settings-btn--secondary" onClick={() => setStep("manage")}>Back</button>
            <button type="submit" className="settings-btn settings-btn--danger" disabled={busy}>Disable MFA</button>
          </div>
        </form>
      )}

      {step === "regenerate" && (
        <form onSubmit={regenerate}>
          <p className="settings-subtitle">Confirm your password and current authenticator code to replace your recovery codes.</p>
          <div className="settings-field">
            <label htmlFor="mfa-regen-password">Password</label>
            <input id="mfa-regen-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <div className="settings-field">
            <label htmlFor="mfa-regen-code">Authenticator code</label>
            <input id="mfa-regen-code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required />
          </div>
          <div className="settings-actions">
            <button type="button" className="settings-btn settings-btn--secondary" onClick={() => setStep("manage")}>Back</button>
            <button type="submit" className="settings-btn settings-btn--primary" disabled={busy}>Generate new codes</button>
          </div>
        </form>
      )}
    </div>
  );
}

function downloadCodes(codes) {
  const blob = new Blob([`${codes.join("\n")}\n`], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "commray-recovery-codes.txt";
  link.click();
  URL.revokeObjectURL(url);
}
